// frontend/src/admin/pages/AdminAiModelsPage.jsx
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

import {
  listAiProviders,
  createAiProvider,
  updateAiProvider,
  deleteAiProvider,
  testAiProvider,
  listProviderModels,
  listAiBindings,
  setAiBinding,
} from "../api";

// Provider kinds: cloud (API key) vs local (base URL the *gateway* must reach).
const KINDS = [
  { value: "openai", label: "OpenAI", local: false },
  { value: "gemini", label: "Google Gemini", local: false },
  { value: "anthropic", label: "Anthropic (Claude)", local: false },
  { value: "ollama", label: "Ollama (local)", local: true },
  { value: "lmstudio", label: "LM Studio (local)", local: true },
];

// Suggested model ids per kind (free-text still allowed via the datalist).
const MODEL_SUGGESTIONS = {
  openai: ["gpt-4o", "gpt-4o-mini", "gpt-4.1"],
  gemini: ["gemini-1.5-flash", "gemini-1.5-pro", "gemini-2.0-flash"],
  anthropic: ["claude-sonnet-4-6", "claude-haiku-4-5-20251001", "claude-opus-4-8"],
  ollama: ["llama3.1:8b", "qwen2.5:14b", "qwen3-coder:30b", "nomic-embed-text"],
  lmstudio: [],
};

// The operator-selectable logical roles (must match ai_gateway.MANAGED_ROLES).
const ROLES = [
  { role: "local/general", label: "General chat", embedding: false },
  { role: "local/coder", label: "Code / technical", embedding: false },
  { role: "local/reason", label: "Reasoning / long-form", embedding: false },
  { role: "local/embed", label: "Embeddings (RAG)", embedding: true },
  { role: "cloud/main", label: "Cloud escalation (main)", embedding: false },
  { role: "cloud/fast", label: "Cloud escalation (fast)", embedding: false },
];

const isLocalKind = (kind) => KINDS.find((k) => k.value === kind)?.local;

function HealthDot({ status }) {
  const color =
    status === "up"
      ? "bg-emerald-500"
      : status === "not_configured"
        ? "bg-amber-400"
        : status === "down"
          ? "bg-rose-500"
          : "bg-slate-300";
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-slate-600">
      <span className={`h-2.5 w-2.5 rounded-full ${color}`} />
      {status || "unknown"}
    </span>
  );
}

/**
 * AdminAiModelsPage — register AI providers (keys stored encrypted server-side,
 * shown masked) and choose which provider/model serves each logical role. A save
 * reconciles into the LiteLLM gateway immediately (no restart) and reports live
 * health. AM4.
 */
function AdminAiModelsPage() {
  const [providers, setProviders] = useState([]);
  const [bindings, setBindings] = useState([]);
  const [providerModels, setProviderModels] = useState({}); // id → string[]
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, b] = await Promise.all([listAiProviders(), listAiBindings()]);
      setProviders(p);
      setBindings(b);
      // Fetch each provider's live model list (best-effort) so selectors show
      // what the provider actually serves, not just static suggestions.
      const entries = await Promise.all(
        p.map(async (pr) => {
          try {
            const r = await listProviderModels(pr.id);
            return [pr.id, r.models || []];
          } catch {
            return [pr.id, []];
          }
        }),
      );
      setProviderModels(Object.fromEntries(entries));
    } catch {
      toast.error("Failed to load AI configuration.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <div className="text-slate-500">Loading AI configuration…</div>;
  }

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h1 className="text-xl font-bold text-slate-800">AI Models</h1>
        <p className="mt-1 text-sm text-slate-500">
          Register providers and choose the active model per role. Changes apply
          to the gateway immediately — backend and agents (n8n) pick them up with
          no restart.
        </p>
      </div>

      <ProvidersSection
        providers={providers}
        bindings={bindings}
        providerModels={providerModels}
        onChange={load}
      />
      <BindingsSection
        providers={providers}
        bindings={bindings}
        providerModels={providerModels}
        onChange={load}
      />
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Providers                                                                    //
// --------------------------------------------------------------------------- //

function ProvidersSection({ providers, bindings, providerModels, onChange }) {
  return (
    <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
      <h2 className="text-base font-semibold text-slate-800 mb-3">Providers</h2>

      {providers.length === 0 ? (
        <p className="text-sm text-slate-500 mb-4">No providers configured yet.</p>
      ) : (
        <table className="w-full text-sm mb-4">
          <thead>
            <tr className="text-left text-slate-500 border-b border-slate-200">
              <th className="py-2 font-medium">Name</th>
              <th className="py-2 font-medium">Kind</th>
              <th className="py-2 font-medium">Key / URL</th>
              <th className="py-2 font-medium">Health</th>
              <th className="py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {providers.map((p) => (
              <ProviderRow
                key={p.id}
                provider={p}
                boundModelIds={bindings
                  .filter((b) => b.provider_id === p.id && !b.is_embedding)
                  .map((b) => b.model_id)}
                liveModels={providerModels[p.id] || []}
                onChange={onChange}
              />
            ))}
          </tbody>
        </table>
      )}

      <AddProviderForm onChange={onChange} />
    </section>
  );
}

function ProviderRow({ provider, boundModelIds = [], liveModels = [], onChange }) {
  const [busy, setBusy] = useState(false);
  const local = isLocalKind(provider.kind);

  const handleTest = async () => {
    // Prefer a model already bound to a role on this provider — that's the
    // model actually serving live traffic, and the one the operator expects
    // "Test" to exercise. Only fall back to an arbitrary live/suggested model
    // (which may include unpulled or chat-incompatible entries the provider
    // merely lists, e.g. Ollama "-cloud" catalog names) when nothing is bound.
    const candidates = boundModelIds.length
      ? boundModelIds
      : liveModels.length
        ? liveModels
        : MODEL_SUGGESTIONS[provider.kind] || [];
    const modelId =
      candidates.find((m) => !/embed/i.test(m)) || candidates[0];
    if (!modelId) {
      toast.error("No models found at this provider — check the URL/key.");
      return;
    }
    setBusy(true);
    try {
      const r = await testAiProvider(provider.id, {
        model_id: modelId,
        is_embedding: false,
      });
      if (r.status === "up")
        toast.success(`${provider.display_name}: reachable (${modelId})`);
      else
        toast.error(
          `${provider.display_name}: ${r.status}${r.error ? ` — ${r.error}` : ""}`,
        );
      onChange();
    } catch {
      toast.error("Test failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleToggle = async () => {
    setBusy(true);
    try {
      await updateAiProvider(provider.id, { enabled: !provider.enabled });
      onChange();
    } catch {
      toast.error("Update failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete provider "${provider.display_name}"?`)) return;
    setBusy(true);
    try {
      await deleteAiProvider(provider.id);
      toast.success("Provider deleted.");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="border-b border-slate-100">
      <td className="py-2 text-slate-800">
        {provider.display_name}
        {!provider.enabled && (
          <span className="ml-2 text-[10px] uppercase font-bold text-amber-600">
            disabled
          </span>
        )}
      </td>
      <td className="py-2 text-slate-600">{provider.kind}</td>
      <td className="py-2 text-slate-500 font-mono text-xs">
        {local ? provider.api_base : provider.api_key_masked || "—"}
      </td>
      <td className="py-2">
        <HealthDot status={provider.last_health} />
      </td>
      <td className="py-2 text-right space-x-2 whitespace-nowrap">
        <button onClick={handleTest} disabled={busy} className="text-indigo-600 hover:underline disabled:opacity-50">
          Test
        </button>
        <button onClick={handleToggle} disabled={busy} className="text-slate-600 hover:underline disabled:opacity-50">
          {provider.enabled ? "Disable" : "Enable"}
        </button>
        <button onClick={handleDelete} disabled={busy} className="text-rose-600 hover:underline disabled:opacity-50">
          Delete
        </button>
      </td>
    </tr>
  );
}

function AddProviderForm({ onChange }) {
  const [kind, setKind] = useState("openai");
  const [displayName, setDisplayName] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [apiBase, setApiBase] = useState("");
  const [busy, setBusy] = useState(false);
  const local = isLocalKind(kind);

  const submit = async (e) => {
    e.preventDefault();
    if (!displayName.trim()) {
      toast.error("Display name is required.");
      return;
    }
    const body = { kind, display_name: displayName.trim() };
    if (local) body.api_base = apiBase.trim();
    else body.api_key = apiKey.trim();

    setBusy(true);
    try {
      await createAiProvider(body);
      toast.success("Provider added.");
      setDisplayName("");
      setApiKey("");
      setApiBase("");
      onChange();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not add provider.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="border-t border-slate-200 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Provider</label>
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          className="w-full rounded-md border-slate-300 text-sm"
        >
          {KINDS.map((k) => (
            <option key={k.value} value={k.value}>{k.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Display name</label>
        <input
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="e.g. OpenAI (prod)"
          className="w-full rounded-md border-slate-300 text-sm"
        />
      </div>
      <div className="sm:col-span-2">
        {local ? (
          <>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Base URL (reachable from the gateway container)
            </label>
            <input
              value={apiBase}
              onChange={(e) => setApiBase(e.target.value)}
              placeholder="http://host.docker.internal:11434"
              className="w-full rounded-md border-slate-300 text-sm font-mono"
            />
          </>
        ) : (
          <>
            <label className="block text-xs font-medium text-slate-600 mb-1">API key</label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="sk-…"
              className="w-full rounded-md border-slate-300 text-sm font-mono"
            />
          </>
        )}
      </div>
      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={busy}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          Add provider
        </button>
      </div>
    </form>
  );
}

// --------------------------------------------------------------------------- //
// Role bindings                                                                //
// --------------------------------------------------------------------------- //

function BindingsSection({ providers, bindings, providerModels, onChange }) {
  const byRole = Object.fromEntries(bindings.map((b) => [b.role, b]));
  const enabledProviders = providers.filter((p) => p.enabled);

  return (
    <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
      <h2 className="text-base font-semibold text-slate-800 mb-1">Active models</h2>
      <p className="text-xs text-slate-500 mb-4">
        Choose the provider + model for each role. Saving applies to the gateway
        immediately.
      </p>
      {enabledProviders.length === 0 ? (
        <p className="text-sm text-amber-600">
          Add and enable a provider above before binding roles.
        </p>
      ) : (
        <div className="space-y-3">
          {ROLES.map((r) => (
            <BindingRow
              key={r.role}
              role={r}
              binding={byRole[r.role]}
              providers={enabledProviders}
              providerModels={providerModels}
              onChange={onChange}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function BindingRow({ role, binding, providers, providerModels, onChange }) {
  const [providerId, setProviderId] = useState(binding?.provider_id || providers[0]?.id || "");
  const [modelId, setModelId] = useState(binding?.model_id || "");
  const [busy, setBusy] = useState(false);

  const selectedProvider = providers.find((p) => p.id === providerId);
  const live = providerModels[providerId] || [];
  // Live models from the provider when available, else static suggestions.
  const options = live.length ? live : MODEL_SUGGESTIONS[selectedProvider?.kind] || [];
  const listId = `models-${role.role.replace("/", "-")}`;

  const save = async () => {
    if (!providerId || !modelId.trim()) {
      toast.error("Pick a provider and model.");
      return;
    }
    setBusy(true);
    try {
      const res = await setAiBinding(role.role, {
        provider_id: providerId,
        model_id: modelId.trim(),
      });
      if (res.health?.status === "up") {
        toast.success(`${role.label}: live`);
      } else {
        toast(`${role.label}: saved, health ${res.health?.status || "unknown"}`, {
          icon: "⚠️",
        });
      }
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply model.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
      <div className="text-sm text-slate-700">
        {role.label}
        <span className="block text-[11px] text-slate-400 font-mono">{role.role}</span>
      </div>
      <select
        value={providerId}
        onChange={(e) => setProviderId(e.target.value)}
        className="rounded-md border-slate-300 text-sm"
      >
        {providers.map((p) => (
          <option key={p.id} value={p.id}>{p.display_name}</option>
        ))}
      </select>
      {live.length ? (
        <select
          value={modelId}
          onChange={(e) => setModelId(e.target.value)}
          className="rounded-md border-slate-300 text-sm font-mono"
        >
          <option value="">— select model —</option>
          {/* keep an existing binding's model visible even if not in the live list */}
          {modelId && !options.includes(modelId) && (
            <option value={modelId}>{modelId} (current)</option>
          )}
          {options.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      ) : (
        <>
          <input
            list={listId}
            value={modelId}
            onChange={(e) => setModelId(e.target.value)}
            placeholder={role.embedding ? "embedding model id" : "model id"}
            className="rounded-md border-slate-300 text-sm font-mono"
          />
          <datalist id={listId}>
            {options.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </>
      )}
      <button
        onClick={save}
        disabled={busy}
        className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
      >
        {binding ? "Update" : "Set"}
      </button>
    </div>
  );
}

export default AdminAiModelsPage;
